import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;

// Robust auth POST that survives service workers, password manager extensions,
// and the dreaded DOMException "The object can not be cloned."
// Strategy: try fetch first; if it throws, fall back to XMLHttpRequest.
const robustPost = (path, body) => {
  const url = `${API}${path}?t=${Date.now()}`; // cache-bust
  const json = JSON.stringify(body);

  const tryFetch = () => fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' },
    credentials: 'include',
    body: json,
  }).then(async (res) => {
    let data = {};
    try { data = await res.json(); } catch { /* empty body */ }
    return { ok: res.ok, status: res.status, data };
  });

  const tryXHR = () => new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.withCredentials = true;
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Cache-Control', 'no-cache');
      xhr.timeout = 20000;
      xhr.onload = () => {
        let data = {};
        try { data = JSON.parse(xhr.responseText || '{}'); } catch { /* empty */ }
        resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.ontimeout = () => reject(new Error('Request timed out'));
      xhr.send(json);
    } catch (e) { reject(e); }
  });

  return tryFetch().catch(() => tryXHR());
};

const robustGet = (path) => {
  const url = `${API}${path}?t=${Date.now()}`;
  const tryFetch = () => fetch(url, { credentials: 'include', headers: { 'Cache-Control': 'no-cache' } })
    .then(async (res) => {
      let data = {};
      try { data = await res.json(); } catch { /* empty */ }
      return { ok: res.ok, status: res.status, data };
    });
  const tryXHR = () => new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.withCredentials = true;
      xhr.setRequestHeader('Cache-Control', 'no-cache');
      xhr.timeout = 15000;
      xhr.onload = () => {
        let data = {};
        try { data = JSON.parse(xhr.responseText || '{}'); } catch { /* empty */ }
        resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.ontimeout = () => reject(new Error('Request timed out'));
      xhr.send();
    } catch (e) { reject(e); }
  });
  return tryFetch().catch(() => tryXHR());
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await robustGet('/api/auth/me');
      setUser(r.ok ? r.data : false);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    let r;
    try {
      r = await robustPost('/api/auth/login', { email, password });
    } catch {
      throw new Error('Connection issue. Please reload the page and try again.');
    }
    if (!r.ok) {
      const msg = typeof r.data?.detail === 'string' ? r.data.detail : 'Invalid email or password.';
      throw new Error(msg);
    }
    setUser(r.data);
    return r.data;
  };

  const register = async (email, password, name) => {
    let r;
    try {
      r = await robustPost('/api/auth/register', { email, password, name });
    } catch {
      throw new Error('Connection issue. Please reload the page and try again.');
    }
    if (!r.ok) {
      const msg = typeof r.data?.detail === 'string' ? r.data.detail : 'Registration failed.';
      throw new Error(msg);
    }
    setUser(r.data);
    return r.data;
  };

  const logout = async () => {
    try { await robustPost('/api/auth/logout', {}); } catch { /* ignore */ }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
